import classNames from 'clsx';
import { camelCase, times } from 'lodash';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';

import { CasinoGameIcon, getIconClassKeys } from '@swf/casino';
import type { CasinoIcon } from '@swf/casino';
import Slider from '@swf/components/Slider/Slider';
import { FeatureFlag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { CasinoGameList as CasinoGameListProps } from '@swf/core/types/CasinoGameList';
import { isGameInfoConfigured, tilesPerRowFromIcon } from '@swf/core/utils/casino';

import CasinoMetaTile from '../CasinoMetaTile/CasinoMetaTile';

import styles from './CasinoGameList.scss';

export type Props = CasinoGameListProps;

const isLive = (icon: CasinoIcon) => icon === 'icnLive';

const CasinoGameList: React.FC<Props> = ({
  placeholder,
  fsbLaunch,
  games,
  loading,
  onLaunch,
  onScroll,
  preload,
  shrink,
  sliderRef,
  race,
  mosaic,
  dynamicColumnFit = false,
  favouriteIcon,
  numbered,
  renderInfo,
  variant,
  ...rest
}) => {
  const isMosaic = !!mosaic?.enabled;
  const slide = !!(isMosaic || sliderRef);
  const icon = isMosaic ? 'icnSquare' : rest.icon;
  const rows = isMosaic ? 2 : rest.rows;
  const tilesPerRow = rest.tilesPerRow || tilesPerRowFromIcon(icon);
  const innerRef = useRef<HTMLDivElement>(null);
  const infoEnabled = useFeature(FeatureFlag.CasinoGameInfo) && isGameInfoConfigured(rest.params);
  const hasInfoRoute = useSelector(select.casino.hasInfoRoute);
  const renderRows = isMosaic ? 2 : Math.min(rows || 0, Math.floor(games.length / tilesPerRow));

  const className = classNames(
    isMosaic && styles.mosaic,
    dynamicColumnFit && styles.dynamicGrid,
    dynamicColumnFit && styles[`dynamic${Math.min(6, games.length)}`],
    slide ? styles.slider : styles.grid,
    getIconClassKeys(icon).map((val: string) => styles[val]),
    numbered && styles.numbered,
    slide && styles[`rows${renderRows}`],
    shrink && styles[`cols${placeholder ? tilesPerRow : Math.min(tilesPerRow, games.length)}`],
    infoEnabled && hasInfoRoute && styles[camelCase(variant)],
    rest.className
  );

  let children = games.map((game, index) => (
    <CasinoMetaTile
      favouriteIcon={favouriteIcon}
      fsbLaunch={fsbLaunch}
      game={game}
      icon={icon}
      index={index}
      slide={slide}
      key={index}
      mosaic={mosaic}
      numbered={numbered}
      onLaunch={onLaunch}
      preload={preload}
      race={race}
      renderInfo={renderInfo}
      count={games.length}
      styles={styles}
      tilesPerRow={tilesPerRow}
      variant={variant}
    />
  ));
  if ((!isMosaic && !games.length) || loading) {
    children = children.concat(
      times((games.length % tilesPerRow) + tilesPerRow * (rows || 1), (count) => (
        <CasinoGameIcon key={games.length + count} />
      ))
    );
  } else if (!isMosaic && placeholder && 'icnSquare' === icon) {
    children = children.concat(
      times(tilesPerRow - games.length, (key) =>
        React.cloneElement(placeholder, { key: games.length + key })
      )
    );
  }

  if (slide) {
    return (
      <Slider
        innerClassName={classNames(className, { [styles.liveCasino]: isLive(icon) })}
        onScroll={onScroll}
        ref={sliderRef}
        innerRef={innerRef}
      >
        {children}
      </Slider>
    );
  }

  return (
    <div className={classNames(className, { [styles.liveCasino]: isLive(icon) })}>{children}</div>
  );
};

export default CasinoGameList;
